import { useEffect } from 'react';
import { Region } from 'wavesurfer.js/src/plugin/regions';
const SRC = 'use-finalcut-ws.ts';

import { RootState } from 'store/store';
import { useAppSelector, useAppDispatch, useAppStore } from 'store/store-hooks';
import { useDraggerContext } from 'components/app/_mediator/app-dragger.provider';
import { setRegions, isRegionIn } from 'components/app/audio-track/_mediator/audio-track.helper';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';
import { isDraggingCell, getCursorCell } from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';
import { setFcActiveClip } from './fc-controls.slice';

export const useFinalcutWs = (ws: WaveSurfer) => {
  const { isDragging } = useDraggerContext();
  const fcCells = useAppSelector((state) => state.fc__data.fcCells);
  const waveIsReady = useAppSelector((state) => state.audio.waveIsReady);
  const dispatch = useAppDispatch();
  const store = useAppStore();

  useEffect(() => {
    if (!fcCells) return;
    const { fcMarkers, fcStoryConfig } = selectData(store.getState());
    setRegions({
      ws,
      markers: fcMarkers,
      ranges: fcCells.duration,
      transitionTime: fcStoryConfig!.transitionTime,
      draggable: false,
    });
  }, [fcCells]);

  useEffect(() => {
    const onSeek = () => {
      const { tracksRestored, fcCell, fcMarkers, duration, fcStoryBoard } = selectData(store.getState());
      const time = ws.getCurrentTime();
      const cell =
        isDragging.current && isDraggingCell(time, fcCell, fcMarkers, duration!)
          ? fcCell
          : getCursorCell(time, fcMarkers);
      const clipId = getFinalcutCursorClip(fcStoryBoard, cell);
      if (tracksRestored && fcCell === cell) return;
      dispatch(setFcActiveClip({ clipId, cell }));
    };
    const onRegionIn = (region: Region) => {
      const { fcCell, fcStoryBoard } = selectData(store.getState());
      const cell = isRegionIn(region, fcCell);
      if (cell) dispatch(setFcActiveClip({ clipId: fcStoryBoard[cell], cell }));
    };
    ws.on('seek', onSeek);
    ws.on('region-in', onRegionIn);
    return () => {
      ws.un('seek', onSeek);
      ws.un('region-in', onRegionIn);
    };
  }, [waveIsReady]);
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { tracksRestored } = state.memory;
  const { fcStoryBoard, fcMarkers, fcStoryConfig } = state.fc__data;
  const { duration } = state.audio;
  const { fcCell } = state.fc__controls;
  return { tracksRestored, fcStoryBoard, duration, fcCell, fcMarkers, fcStoryConfig };
};

/* H E L P E R */

const getFinalcutCursorClip = (storyBoard: CellClip[], cell: Index): CellClip => {
  const clipId = storyBoard[cell];
  if (clipId === undefined) {
    console.error(`[${SRC}] getFinalcutCursorClip: clipId is not defined`);
    return null;
  }
  return clipId === EMPTY_CELL ? null : clipId;
};
